import * as axios from '../../axios-extended'
import * as t from 'io-ts'
import {
  array,
  taskEither,
  record,
  nonEmptyArray,
  ord,
  option,
  number,
} from 'fp-ts'
import { pipe, tuple } from 'fp-ts/lib/function'
import { Transaction, CashoutRequest } from './types'
import { optionFromNullable } from 'io-ts-types/lib/optionFromNullable'
import { DateFromNumber } from 'io-ts-types/lib/DateFromNumber'
import { withFallback } from 'io-ts-types'

const apiUrl = '/prosperbill/api'

export const getDCNNymID = (cancelToken?: axios.CancelToken) =>
  axios.get('/dcn/dcn1.0/nym-id', { cancelToken, decoder: t.string })

export const CashoutMethod = t.keyof({
  KIKO: null,
  PAYPAL: null,
  PAYMEE: null,
  TRANSFER: null,
})
export type CashoutMethod = t.TypeOf<typeof CashoutMethod>

const PBLTransaction = t.type(
  {
    id: t.number,
    peers: t.record(
      t.string,
      t.type(
        {
          transferQuantity: t.record(t.string, t.number),
        },
        'Peer'
      )
    ),
    notarizationResult: optionFromNullable(
      t.type(
        {
          success: t.boolean,
        },
        'TransactionResult'
      )
    ),
    createdAt: DateFromNumber,
    order: optionFromNullable(
      t.type({ invoiceID: t.number, invoiceReference: t.string })
    ),
    cashOut: optionFromNullable(
      t.type({
        id: t.number,
        reference: t.string,
        recipient: optionFromNullable(t.string),
        method: CashoutMethod,
      })
    ),
  },
  'PBLTransaction'
)

type PBLTransaction = t.TypeOf<typeof PBLTransaction>

const PBLTransactionsData = t.type({
  count: t.number,
  data: t.array(PBLTransaction),
})

const transactionType = (
  userNymID: string,
  ps: PBLTransaction['peers']
): Transaction['type'] =>
  pipe(
    record.lookup(userNymID, ps),
    option.map(p =>
      pipe(
        record.toArray(p.transferQuantity),
        array.map(x => x[1]),
        array.reduce(
          { positive: false, negative: false },
          ({ positive, negative }, a) =>
            a < 0 ? { positive, negative: true } : { positive: true, negative }
        ),
        ({ positive, negative }) =>
          positive && negative ? 'exchange' : positive ? 'incoming' : 'outgoing'
      )
    ),
    option.getOrElse((): Transaction['type'] => 'incoming')
  )

const pblTransactionToTransaction =
  (userNymID: string) =>
  ({
    id,
    createdAt,
    peers,
    notarizationResult,
    order,
    cashOut,
  }: PBLTransaction): Transaction => {
    const [unit, amount] = pipe(
      record.lookup(userNymID, peers),
      option.map(p =>
        pipe(
          p.transferQuantity,
          record.toArray,
          array.map(([unitID, a]) => tuple(unitID, Math.abs(a)))
        )
      ),
      option.getOrElse((): [string, number][] => []),
      array.prepend(tuple('', 0)),
      nonEmptyArray.max(
        pipe(
          number.Ord,
          ord.contramap(v => v[1])
        )
      )
    )
    return {
      id,
      createdAt,
      cashOut,
      unit,
      amount,
      rib: pipe(
        cashOut,
        option.filter(co => co.method === 'KIKO'),
        option.chain(co => co.recipient),
        option.getOrElse(() => 'N/A')
      ),
      status: pipe(
        notarizationResult,
        option.map((r): Transaction['status'] =>
          r.success ? 'success' : 'failed'
        ),
        option.getOrElse((): Transaction['status'] => 'pending')
      ),
      invoice: pipe(
        order,
        option.map(o => ({ id: o.invoiceID, reference: o.invoiceReference }))
      ),
      type: transactionType(userNymID, peers),
    }
  }

export const getTransactions = (
  organization: string,
  { page = 0, itemsPerPage = 10 },
  userNymID: string,
  cancelToken?: axios.CancelToken
) =>
  pipe(
    axios.get(`${apiUrl}/transactions/${organization}`, {
      cancelToken,
      decoder: PBLTransactionsData,
      params: {
        page,
        size: itemsPerPage,
        sort: 'createdAt,desc',
      },
    }),
    taskEither.map(({ count, data }) => ({
      count,
      data: pipe(data, array.map(pblTransactionToTransaction(userNymID))),
    }))
  )

const CashOut = t.type(
  {
    id: t.number,
    reference: t.string,
    status: t.string,
  },
  'CashOut'
)

export const collectFunds = (
  organization: string,
  req: CashoutRequest,
  cancelToken?: axios.CancelToken
) =>
  axios.put(`${apiUrl}/cash-out/${organization}`, req, {
    cancelToken,
    decoder: CashOut,
  })

export const cashoutMethodsSupportedAssets = (
  cancelToken?: axios.CancelToken
) =>
  axios.get(`${apiUrl}/cash-out/supported-assets`, {
    cancelToken,
    decoder: t.record(CashoutMethod, withFallback(t.array(t.string), [])),
  })
